import { setLanguage, setSession } from 'src/store/session/sessionSlice';
import { AppDispatch } from 'src/store/rootStore';
import { PostMessageKey } from 'src/enums/post-message-key.enum';
import settingsService from 'src/services/settings/settings.service';
import {
    ITeroCtx,
    getRefreshTokenUrl,
    Session,
    User,
    BizCtx,
} from 'src/interfaces/iTeroContext';
import i18n from 'i18next';
import { v4 as uuid } from 'uuid';
import biLoggingService, {
    ErrorReportData,
    SessionStartData,
    SeverityType,
} from 'src/services/bi-logging/bi-logging.service';
import { sendError } from '../event-message/event-message.service';
import { ErrorCode } from 'src/enums/error-code.enum';
import { Buffer } from 'buffer';
import { SessionState } from 'src/store/session/sessionState.model';

class MessageListener {
    private readonly eventTypeMessage = 'message';
    private dispatch: AppDispatch;
    private readonly errorText = 'SharedDesignError.Retryable.Text';
    private readonly translation = i18n.t;
    private isNewContext: boolean;

    async init(dispatch: AppDispatch) {
        const { isNewContext } = await settingsService.getSettings();
        this.isNewContext = isNewContext;
        window.addEventListener(this.eventTypeMessage, this.handleMessage, true);
        this.dispatch = dispatch;
    }

    private handleMessage = (message: MessageEvent) => {
        if (message.data?.eventName === PostMessageKey.InitContext) {
            this.tryToInitContext(message.data.payload);
        }

        if (message.data?.eventName === PostMessageKey.LocaleChanged) {
            const { language } = message.data.payload;
            i18n.changeLanguage(language);
            this.dispatch(setLanguage(language));
        }
    };

    private tryToInitContext(payload: any) {
        try {
            const sessionState = this.getSessionState(payload);
            this.validateInitContext(sessionState);
            i18n.changeLanguage(sessionState.selectedLanguage);
            this.dispatch(setSession(sessionState));

            const sessionStartData: SessionStartData = {
                orderId: sessionState.orderId,
                csmMessageId: sessionState.csmMessageId,
                userId: sessionState.doctorId.toString(),
                companyId: sessionState.companyId.toString(),
            };
            biLoggingService.sendSessionStart(sessionState.externalSessionId, sessionStartData);
        } catch (err) {
            sendError({
                errorCode: ErrorCode.InitContext,
                errorHeader: this.translation(this.errorText),
            });

            const errorData: ErrorReportData = {
                object: 'get initContext',
                code: '',
                description: err,
                severity: SeverityType.Error,
            };

            biLoggingService.sendErrorReport(errorData);
            console.log(`${err}: `, payload);
        }
    }

    private getSessionState(payload: any): SessionState {
        const internalSessionId = uuid();
        if (!this.isNewContext) {
            const bizContextBase64 = this.createBizContextBase64(payload);
            const {
                orderId,
                sessionId,
                csmMessageId,
                accessToken,
                selectedLanguage,
                refreshTokenUrl,
                selectedCompanyId,
                selectedDoctorId,
            } = payload;

            return {
                sessionId: internalSessionId,
                externalSessionId: sessionId,
                companyId: selectedCompanyId,
                doctorId: selectedDoctorId,
                orderId,
                csmMessageId,
                accessToken,
                refreshTokenUrl,
                selectedLanguage,
                bizContextBase64,
            } as SessionState;
        } else {
            const { session, user, bizCtx, UI, security, platformServices } = payload as ITeroCtx;
            const bizContextBase64 = this.createNewBizContextBase64(session, user, bizCtx);
            return {
                sessionId: internalSessionId,
                externalSessionId: session.sessionId,
                csmMessageId: bizCtx.csmMessageId,
                doctorId: bizCtx.contactId,
                companyId: bizCtx.companyId,
                orderId: bizCtx.orderId,
                accessToken: security.accessToken,
                refreshTokenUrl: getRefreshTokenUrl(platformServices),
                selectedLanguage: UI.languague,
                bizContextBase64,
            } as SessionState;
        }
    }

    private validateInitContext(sessionState: SessionState) {
        if (
            sessionState.accessToken === undefined ||
            sessionState.csmMessageId === undefined ||
            sessionState.orderId === undefined
        ) {
            throw new Error('not all essential properties contain in context');
        }
    }

    private createNewBizContextBase64(session: Session, user: User, bizCtx: BizCtx) {
        try {
            const btzContext = {
                session: {
                    sessionId: session.sessionId?.toString() ?? '',
                    sessionType: session.sessionType?.toString() ?? '',
                },
                user: {
                    loggedInUserId: user.loggedInUserId?.toString() ?? '',
                    roleType: user.roleType?.toString() ?? '',
                },
                device: {
                    scannerModel: '',
                    scannerVersion: '',
                    scannerSerial: '',
                    wandSerial: '',
                },
                bizCtx: {
                    contactId: bizCtx.contactId?.toString() ?? '',
                    companyId: bizCtx.companyId?.toString() ?? '',
                    region: bizCtx.region?.toString() ?? '',
                    patientId: bizCtx.patientId?.toString() ?? '',
                    orderId: bizCtx.orderId?.toString() ?? '',
                },
            };

            return Buffer.from(JSON.stringify(btzContext)).toString('base64');
        } catch (e) {
            console.log('Failed create bizContextBase64' + e);
            return null;
        }
    }

    private createBizContextBase64(payload: any): string {
        try {
            const btzContext = {
                session: {
                    sessionId: payload.sessionId?.toString() ?? '',
                    sessionType: payload.sessionType?.toString() ?? ''
                },
                user: {
                    loggedInUserId: payload.loggedInUserId?.toString() ?? '',
                    roleType: payload.roleType?.toString() ?? ''
                },
                device: {
                    scannerModel: '',   
                    scannerVersion: '',
                    scannerSerial: '',
                    wandSerial: '',
                },
                bizCtx: {
                    contactId: payload.selectedDoctorId?.toString() ?? '',
                    companyId: payload.selectedCompanyId?.toString() ?? '',
                    region: payload.region?.toString() ?? '',
                    patientId: payload.patientId?.toString() ?? '',
                    orderId: payload.orderId?.toString() ?? '',
                },
            };

            return Buffer.from(JSON.stringify(btzContext)).toString('base64');
        } catch (e) {
            console.log('Failed create bizContextBase64' + e);
            return null;
        }
    }
}

export default new MessageListener();
